import { BookOpen, MessageCircle, FileText } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { Sentiment, TimelineItem } from './types';

type Props = {
  items: TimelineItem[];
  windowStart?: string | null;
  windowEnd?: string | null;
};

const TYPE_ICON = {
  diary: BookOpen,
  chat: MessageCircle,
  note: FileText,
} as const;

const DAY_LABELS = ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'] as const;

function extractSentimentLabel(sentiment: Sentiment): string | null {
  if (!sentiment) return null;
  if (typeof sentiment === 'string') return sentiment;
  if (typeof sentiment === 'object' && 'label' in sentiment) {
    return sentiment.label;
  }
  return null;
}

function sentimentDotColor(sentiment: Sentiment): string {
  const label = extractSentimentLabel(sentiment);
  if (!label) return 'text-muted-foreground';
  const s = label.toLowerCase();
  if (s === 'positive' || s === 'positivo') return 'text-green-500';
  if (s === 'negative' || s === 'negativo') return 'text-emergency';
  if (s === 'mixed' || s === 'misto' || s === 'neutral' || s === 'neutro') return 'text-amber-500';
  return 'text-muted-foreground';
}

type DayBucket = {
  date: string;
  dayLabel: string;
  dayNum: string;
  items: TimelineItem[];
};

function bucketByDay(
  items: TimelineItem[],
  windowStart?: string | null,
  windowEnd?: string | null,
): DayBucket[] {
  const map = new Map<string, TimelineItem[]>();

  for (const item of items) {
    const existing = map.get(item.date);
    if (existing) {
      existing.push(item);
    } else {
      map.set(item.date, [item]);
    }
  }

  const itemDates = [...new Set(items.map((i) => i.date))].sort();
  const startStr = windowStart ?? itemDates[0];
  const endStr = windowEnd ?? itemDates[itemDates.length - 1];
  if (!startStr || !endStr) return [];

  const start = new Date(startStr);
  const end = new Date(endStr);

  const buckets: DayBucket[] = [];
  const current = new Date(start);

  while (current <= end) {
    const iso = current.toISOString().slice(0, 10);
    buckets.push({
      date: iso,
      dayLabel: DAY_LABELS[current.getDay()] ?? 'Dom',
      dayNum: String(current.getDate()).padStart(2, '0'),
      items: map.get(iso) || [],
    });
    current.setDate(current.getDate() + 1);
  }

  return buckets;
}

export function ReviewTimeline({ items, windowStart, windowEnd }: Props) {
  if (items.length === 0) return null;

  const buckets = bucketByDay(items, windowStart, windowEnd);

  return (
    <Card variant="elevated">
      <CardHeader>
        <CardTitle headingLevel="h3">Cronologia</CardTitle>
      </CardHeader>
      <CardContent>
        <TooltipProvider delayDuration={200}>
          <div className="overflow-x-auto -mx-2 px-2 pb-2">
            <div className="flex gap-1 min-w-max">
              {buckets.map((bucket) => (
                <div key={bucket.date} className="flex flex-col items-center gap-1.5 w-14 shrink-0">
                  <span className="text-[10px] text-muted-foreground leading-none">
                    {bucket.dayLabel}
                  </span>
                  <span className="text-xs font-medium leading-none">{bucket.dayNum}</span>

                  <div className="flex flex-col items-center gap-1 min-h-[40px] justify-start pt-1">
                    {bucket.items.length === 0 ? (
                      <div className="w-px h-6 border-l border-dashed border-muted-foreground/30" />
                    ) : (
                      bucket.items.map((item, j) => {
                        const Icon = TYPE_ICON[item.type] || TYPE_ICON.note;
                        const color = sentimentDotColor(item.sentiment);
                        return (
                          <Tooltip key={`${item.type}-${item.id}-${j}`}>
                            <TooltipTrigger asChild>
                              <button
                                type="button"
                                className={`${color} hover:scale-125 transition-transform`}
                              >
                                <Icon className="size-4" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent side="bottom" className="max-w-[200px]">
                              <p className="text-xs">
                                {item.summary_preview || 'Nessuna nota per questa voce.'}
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        );
                      })
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </TooltipProvider>
      </CardContent>
    </Card>
  );
}
